import { computed, getCurrentInstance, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { checkPassword, isPasswordValid } from 'src/utils/passwordPolicy'
import { readApiFailure } from 'src/composables/useMagicLink'

// Set-password step of the magiclink flow (`/set-password?magic=<token>`).
// Ported from quasar.sowell-extension.auth/src/composables/useValidateMagicLink.ts.

export function useValidateMagicLink(magicToken) {
  const { t } = useI18n()
  const router = useRouter()
  const proxy =
    getCurrentInstance().appContext.config.globalProperties.$SowellProxy

  const password = ref('')
  const confirmPassword = ref('')
  const showPassword = ref(false)
  const showConfirmPassword = ref(false)
  const loading = ref(false)
  const passwordUpdated = ref(false)
  const linkExpired = ref(false)
  const serverError = ref('')

  const passwordRequirements = computed(() =>
    checkPassword(password.value).map((requirement) => ({
      ...requirement,
      label: t(requirement.labelKey)
    }))
  )

  // Shown live under the confirmation field (and turns it red) as soon as the
  // two entries diverge - the requirements checklist covers everything else.
  const confirmPasswordError = computed(() =>
    confirmPassword.value !== '' && confirmPassword.value !== password.value
      ? t('login.errorSamePasswordValidation')
      : ''
  )

  const canSubmit = computed(
    () =>
      isPasswordValid(password.value) &&
      confirmPassword.value !== '' &&
      confirmPassword.value === password.value
  )

  const onSubmit = async () => {
    if (!canSubmit.value) {
      serverError.value = isPasswordValid(password.value)
        ? t('login.errorSamePasswordValidation')
        : t('login.passwordInvalidFormat')
      return
    }

    serverError.value = ''
    loading.value = true
    try {
      await proxy.validateMagicLink(magicToken.value, password.value)
      passwordUpdated.value = true
    } catch (error) {
      const { status } = readApiFailure(error)
      // 401 covers both an expired link and one that was already consumed.
      if (status === 401) {
        linkExpired.value = true
      } else if (status === 422) {
        serverError.value = t('login.passwordInvalidFormat')
      } else {
        serverError.value = t('login.errorUnprocessableRequest')
      }
    } finally {
      loading.value = false
    }
  }

  const goToLoginPage = () => {
    router.push({ path: '/login' })
  }

  const goToFirstLogin = () => {
    router.push({ path: '/first-login', query: { context: 'activate' } })
  }

  return {
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    loading,
    passwordUpdated,
    linkExpired,
    serverError,
    passwordRequirements,
    confirmPasswordError,
    canSubmit,
    onSubmit,
    goToLoginPage,
    goToFirstLogin
  }
}
